import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, TextField, MenuItem, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, Snackbar, Alert, FormControl, InputLabel, Select
} from '@mui/material';
import { Add as AddIcon, Visibility as ViewIcon, Cancel as CancelIcon, ArrowBack as ArrowBackIcon, Calculate as CalculateIcon, Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import AlbaranEstadoBadge from '../components/AlbaranEstadoBadge';
import CalculadoraCambioModal from '../components/CalculadoraCambioModal'; // ajusta la ruta según donde lo tengas
import ConsultaPrecios from '../components/ConsultaPrecios'; // ajusta la ruta

const obtenerSucursalToken = () => {
  const token = localStorage.getItem('token');
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const sucursalId = payload.id_sucursal || payload.sucursal_id || payload.idSucursal || null;
      return sucursalId ? String(sucursalId) : '';
    } catch (err) {
      console.error('Error al leer sucursal del token:', err);
    }
  }
  return '';
};

const AlbaranesGestion = () => {
  const [albaranes, setAlbaranes] = useState([]);
  const [estadoFiltro, setEstadoFiltro] = useState('');
  const [busqueda, setBusqueda] = useState('');

  const [sucursalUsuario] = useState(obtenerSucursalToken);
  const [sucursalFiltro, setSucursalFiltro] = useState(obtenerSucursalToken);
  const [sucursales, setSucursales] = useState([]);

  const [modalAnularOpen, setModalAnularOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [motivoAnulacion, setMotivoAnulacion] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', severity: 'success' });

  const [modalCambioOpen, setModalCambioOpen] = useState(false);

  const [modalPreciosOpen, setModalPreciosOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSucursales = async () => {
      try {
        const res = await api.get('/sucursales/');
        setSucursales(res.data || []);
      } catch (err) {
        console.error('Error al cargar sucursales:', err);
      }
    };
    fetchSucursales();
  }, []);

  const fetchAlbaranes = async () => {
    try {
      const params = {};
      if (estadoFiltro) params.estado_albaran = estadoFiltro;
      if (sucursalFiltro) params.id_sucursal = sucursalFiltro;

      const res = await api.get('/albaranes/', { params });
      setAlbaranes(res.data || []);
    } catch (err) {
      setSnackbar({ open: true, msg: 'Error al cargar albaranes', severity: 'error' });
    }
  };

  // comandos rápidos: F2 = Calculadora, F3 = Lista Precios
  useEffect(() => {
    const handleKeyDown = (e) => {
      // F2 o Ctrl + C → Calculadora de cambio
      if (e.key === 'F2' || (e.ctrlKey && e.key.toLowerCase() === 'c')) {
        e.preventDefault();
        setModalCambioOpen(true);
      }

      // F3 → Lista de Precios
      if (e.key === 'F3') {
        e.preventDefault();
        setModalPreciosOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // 1. Invocar la carga de datos al montar la vista y al cambiar filtros
useEffect(() => {
  fetchAlbaranes();
}, [estadoFiltro, sucursalFiltro]);

  const handleAnular = async () => {
    try {
      await api.post(`/albaranes/${selectedId}/anular`, { motivo: motivoAnulacion });
      setSnackbar({ open: true, msg: 'Albarán anulado exitosamente', severity: 'success' });
      setModalAnularOpen(false);
      fetchAlbaranes();
    } catch (err) {
      setSnackbar({ open: true, msg: err.response?.data?.detail || 'Error al anular', severity: 'error' });
    }
  };

  const albaranesFiltrados = albaranes.filter((row) => {
    if (!busqueda.trim()) return true;
    const query = busqueda.toLowerCase();
    const cliente = (row.nombre_cliente || '').toLowerCase();
    const paciente = (row.nombre_paciente || '').toLowerCase();
    return cliente.includes(query) || paciente.includes(query);
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/clientes')}>
            Volver
          </Button>

          <Typography variant="h5" fontWeight="bold">
            Gestión de Albaranes
          </Typography>
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        <Button
          variant="outlined"
          startIcon={<SearchIcon />}  // o el ícono que prefieras
          onClick={() => setModalPreciosOpen(true)}
        >
          Lista Precios (F3)
        </Button>

        {/* Botón de la calculadora */}
        <Button
          variant="outlined"
          startIcon={<CalculateIcon />}
          onClick={() => setModalCambioOpen(true)}
        >
          Cambio (F2)
        </Button>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/albaranes/detalle/nuevo')}
        >
          Nuevo Albarán
        </Button>
    </Box>

      {/* Barra de Filtros */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label="Buscar cliente o paciente"
          variant="outlined"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          sx={{ minWidth: 250 }}
        />

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Sucursal</InputLabel>
          <Select
            value={sucursalFiltro}
            label="Sucursal"
            onChange={(e) => setSucursalFiltro(e.target.value)}
          >
            <MenuItem value=""><em>Todas las Sucursales</em></MenuItem>
            {sucursales.map((s) => (
              <MenuItem key={s.id_sucursal} value={String(s.id_sucursal)}>
                {s.nombre_sucursal || s.nombre}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          select label="Filtrar por Estado"
          value={estadoFiltro} onChange={(e) => setEstadoFiltro(e.target.value)} 
          size="small" sx={{ minWidth: 180 }}
        >
          <MenuItem value="">TODOS</MenuItem>
          <MenuItem value="ABIERTO">ABIERTO</MenuItem>
          <MenuItem value="CERRADO">CERRADO</MenuItem>
          <MenuItem value="FACTURADO">FACTURADO</MenuItem>
          <MenuItem value="ANULADO">ANULADO</MenuItem>
        </TextField>

        {sucursalFiltro && sucursalUsuario && sucursalFiltro === sucursalUsuario && (
          <Typography variant="caption" color="text.secondary">
            (tu sucursal por defecto)
          </Typography>
        )}
      </Box>

      {/* Tabla de Albaranes */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: '#f8fafc' }}>
            <TableRow>
              <TableCell>Fecha Apertura</TableCell>
              <TableCell>Días</TableCell>          
              <TableCell>Cliente / Paciente (Referencia)</TableCell>
              <TableCell>Número</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="right">Abonado</TableCell>
              <TableCell align="right">Saldo</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {albaranesFiltrados.map((row) => {
              // Cálculo de días desde la apertura
              const dias = (() => {
                if (!row.fecha_apertura) return '—';
                const apertura = new Date(row.fecha_apertura);
                const hoy = new Date();
                // Normalizamos a medianoche para contar días completos
                apertura.setHours(0, 0, 0, 0);
                hoy.setHours(0, 0, 0, 0);
                const diffMs = hoy - apertura;
                return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
              })();

              return (
                <TableRow key={row.id_albaran}>
                  <TableCell>
                    {(() => {
                      if (!row.fecha_apertura) return '—';
                      const d = new Date(row.fecha_apertura);
                      const meses = [
                        'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
                        'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
                      ];
                      return `${d.getDate()}-${meses[d.getMonth()]}-${d.getFullYear()}`;
                    })()}
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      fontWeight={dias >= 7 ? 600 : 400}
                      color={dias >= 15 ? 'error.main' : dias >= 7 ? 'warning.main' : 'text.primary'}
                    >
                      {dias} {dias === 1 ? 'día' : 'días'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {row.nombre_cliente}{' '}
                    {row.nombre_paciente ? `(${row.nombre_paciente})` : ''}
                  </TableCell>
                  <TableCell>{row.numero_albaran || `ID: ${row.id_albaran}`}</TableCell>
                  <TableCell>
                    <AlbaranEstadoBadge estado={row.estado_albaran} />
                  </TableCell>
                  <TableCell align="right">
                    ${row.total_albaran ? parseFloat(row.total_albaran).toFixed(2) : '0.00'}
                  </TableCell>
                  <TableCell align="right">
                    ${row.total_abonado ? parseFloat(row.total_abonado).toFixed(2) : '0.00'}
                  </TableCell>
                  <TableCell align="right">
                    ${row.saldo_pendiente ? parseFloat(row.saldo_pendiente).toFixed(2) : '0.00'}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="primary"
                      onClick={() => navigate(`/albaranes/detalle/${row.id_albaran}`)}
                    >
                      <ViewIcon />
                    </IconButton>
                    {row.estado_albaran !== 'ANULADO' && row.estado_albaran !== 'FACTURADO' && (
                      <IconButton
                        color="error"
                        onClick={() => {
                          setSelectedId(row.id_albaran);
                          setModalAnularOpen(true);
                        }}
                      >
                        <CancelIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>


      <ConsultaPrecios
        open={modalPreciosOpen}
        onClose={() => setModalPreciosOpen(false)}
      />


        {/* Modal Calculadora de Cambio */}
        <CalculadoraCambioModal
          open={modalCambioOpen}
          onClose={() => setModalCambioOpen(false)}
          // No pasamos totalPagar → el usuario puede ingresarlo libremente
        />
      {/* Modal de Anulación */}
      <Dialog open={modalAnularOpen} onClose={() => setModalAnularOpen(false)}>
        <DialogTitle>Anular Albarán</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth label="Motivo de Anulación" margin="dense" multiline rows={3}
            value={motivoAnulacion} onChange={(e) => setMotivoAnulacion(e.target.value)} required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalAnularOpen(false)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleAnular} disabled={!motivoAnulacion.trim()}>Anular</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AlbaranesGestion;